<template>
  <div v-if="inline" class="c-search-filter--inline flex flex-col bg-base" :v-bind="$attrs">
    <span class="flex items-center px-6 font-size-4">
      <span>{{ name }}</span>
      <span v-if="modelValue?.length" class="ml-2 cursor-pointer underline font-size-3" @click="onClearSearch">
        {{ t('canvasUI.components.filters.clear') }}
      </span>
    </span>
    <div class="mb-2 py-2 px-6">
      <template v-if="!isCustom && !!pitcherSettings.enable_search_filter_with_suggestions">
        <!-- Use NSelect when all data is available locally -->
        <NSelect
          v-if="!hasMoreData && distinctValues.length > 0"
          class="max-h-[36px] border-text3 border-solid rounded-1 box-border"
          :clearable="!!modelValue"
          :consistent-menu-width="false"
          :disabled="disabled"
          filterable
          :loading="isLoading"
          :options="selectOptions"
          :placeholder="t('canvasUI.components.filters.searchPlaceholder')"
          :value="modelValue"
          @update:value="onSelectUpdate"
        />
        <!-- Use search component when need to fetch from server -->
        <CSearchOnClickWithSuggestions
          v-else
          ref="searchOnClickRef"
          :disabled="disabled"
          :initial-search-value="modelValue"
          :min-search-length="numeric ? 1 : 3"
          :placeholder="t('canvasUI.components.filters.searchPlaceholder')"
          :suggestions-fetcher="fetchDistinctValueSuggestions"
          @search="onSearch"
        />
      </template>
      <template v-else>
        <CSearchOnClick
          ref="searchOnClickRef"
          :disabled="disabled"
          :initial-search-value="modelValue"
          :placeholder="t('canvasUI.components.filters.searchPlaceholder')"
          @search="onSearch"
        />
      </template>
    </div>
  </div>
  <NPopover
    v-else
    class="c-search-filter"
    :on-clickoutside="onOutsideClick"
    placement="bottom-start"
    raw
    :show="isOpen"
    :show-arrow="false"
    trigger="manual"
  >
    <template #trigger>
      <NTag
        class="select-none cursor-pointer"
        :style="{
          '--n-color': modelValue ? themeVars?.primary5 : themeVars?.primary6,
          '--n-border': `1px solid ${themeVars?.primary5}`,
          '--n-height': '28px',
        }"
        :themeOverrides="{
          borderRadius: '4px',
        }"
        @click="isOpen = !isOpen"
      >
        <div class="flex items-center">
          <div v-if="!modelValue">
            {{ filterName }}
          </div>
          <div v-else class="font-bold">
            {{ `${filterName}:` }}
          </div>
          <div v-if="modelValue" class="ml-1">
            {{ modelValue }}
          </div>
        </div>
      </NTag>
    </template>
    <div class="c-search-filter__popover w-[350px] rounded bg-base" :v-bind="$attrs">
      <div class="pa-2">
        <template v-if="!isCustom && !!pitcherSettings.enable_search_filter_with_suggestions">
          <!-- Use NSelect when all data is available locally -->
          <NSelect
            v-if="!hasMoreData && distinctValues.length > 0"
            class="max-h-[36px] border-text3 border-solid rounded-1 box-border"
            :clearable="!!modelValue"
            :consistent-menu-width="false"
            :disabled="disabled"
            filterable
            :loading="isLoading"
            :options="selectOptions"
            :placeholder="t('canvasUI.components.filters.searchPlaceholder')"
            :value="modelValue"
            @update:value="onSelectUpdate"
          />
          <!-- Use search component when need to fetch from server -->
          <CSearchOnClickWithSuggestions
            v-else
            ref="searchOnClickRef"
            :disabled="disabled"
            :initial-search-value="modelValue"
            :min-search-length="numeric ? 1 : 3"
            :placeholder="t('canvasUI.components.filters.searchPlaceholder')"
            :suggestions-fetcher="fetchDistinctValueSuggestions"
            @search="onSearch"
          />
        </template>
        <template v-else>
          <CSearchOnClick
            ref="searchOnClickRef"
            :disabled="disabled"
            :initial-search-value="modelValue"
            :placeholder="t('canvasUI.components.filters.searchPlaceholder')"
            @search="onSearch"
          />
        </template>
      </div>
      <div class="pa-2" :style="{ borderTop: `1px solid ${themeVars.primary5}` }">
        <CButton block class="pa-2" :disabled="disabled" text @click="onClearSearch">
          {{ t('canvasUI.components.filters.clearSearch') }}
        </CButton>
      </div>
    </div>
  </NPopover>
</template>

<script lang="ts" setup>
import { ref, computed, inject, onMounted, watch, onUnmounted, type ComputedRef, type Ref } from 'vue'
import { NTag, NPopover, NSelect } from 'naive-ui'
import { useI18n } from 'vue-i18n'
import type { AxiosInstance } from 'axios'

import CButton from '@lib/components/CButton/CButton.vue'
import CSearchOnClick from '@lib/components/CSearch/CSearchOnClick.vue'
import CSearchOnClickWithSuggestions from '@lib/components/CSearch/CSearchOnClickWithSuggestions.vue'
import useThemeVars from '@lib/composables/useThemeVars'
import useMetadataSearch from '@lib/composables/useMetadataSearch'
import useFilterSuggestions, { type FilterSuggestion } from '@lib/composables/useFilterSuggestions'
import type { OrganizationSettings } from '@lib/types/organizationSettings.types'
import type { InstanceSettings } from '@lib/types/instanceSettings.types'
import type { MetadataFilterRequest } from '@lib/types/core/MetadataFilter'
import type { StyledAutoCompleteOption } from '@lib/types/search'

type Props = {
  disabled?: boolean
  entity?: MetadataFilterRequest['entity']
  inline?: boolean
  includeDraftValues?: boolean
  name?: string
  numeric?: boolean
  modelValue?: string
  filterKey?: string
  isCustom?: boolean
  isSection?: boolean
  isTemplate?: boolean
  ownerId?: number | null
  limit?: number
  externalFilters?: Record<string, any>
  isDependentField?: boolean
  systemFilteredIds?: string[]
  tagsNotIn?: string[]
}

const props = withDefaults(defineProps<Props>(), {
  disabled: false,
  inline: false,
  name: '',
  numeric: false,
  modelValue: '',
  filterKey: '',
  includeDraftValues: false,
  isCustom: false,
  isSection: false,
  isTemplate: false,
  ownerId: null,
  limit: 100,
  externalFilters: () => ({}),
  isDependentField: false,
  systemFilteredIds: undefined,
  tagsNotIn: () => [],
})

const emit = defineEmits<{
  (e: 'update:modelValue', v: string): void
  (e: 'clear'): void
}>()

const themeVars = useThemeVars()
const { t } = useI18n()
const pitcherSettings = inject<ComputedRef<OrganizationSettings & InstanceSettings>>(
  'pitcherSettings',
  computed(() => ({})),
)
const pitcherInfo = inject<ComputedRef<{ instanceId: string }>>(
  'pitcherInfo',
  computed(() => ({ instanceId: '' })),
)
const coreRestApiAxios = inject<Ref<AxiosInstance>>('coreRestApiAxios')

const searchOnClickRef = ref<InstanceType<typeof CSearchOnClick | typeof CSearchOnClickWithSuggestions> | null>(null)
const isOpen = ref(false)

// Use the new filter suggestions composable
const filterSuggestions = useFilterSuggestions({
  entity: props.entity,
  filterKey: props.filterKey,
  includeDraftValues: props.includeDraftValues,
  isSection: props.isSection,
  isTemplate: props.isTemplate,
  ownerId: props.ownerId,
  limit: props.limit,
  tagsNotIn: props.tagsNotIn,
})

const { isEnabled, debouncedFetch, cleanup, isLoading } = filterSuggestions

// Use existing metadata search for search suggestions
const { handleFetch } = useMetadataSearch({ includeSearchTermAsOption: true })

// State for tracking distinct values and has_more
const distinctValues = ref<FilterSuggestion[]>([])
const hasMoreData = ref(false)

// Helper function to determine what constraints to use
function getConstraintFilters(): Record<string, any> {
  // Only use external filters for dependent fields
  // Non-dependent fields should not be constrained by other filters
  return props.isDependentField ? props.externalFilters || {} : {}
}

// Filter suggestions based on system filter IDs
async function filterSuggestionsBySystemFilters(suggestions: FilterSuggestion[]): Promise<FilterSuggestion[]> {
  // If no system filters are active (undefined), return all suggestions
  if (props.systemFilteredIds === undefined) {
    return suggestions
  }

  // If system filters are active but yielded no results (empty array), return empty
  if (props.systemFilteredIds.length === 0) {
    return []
  }

  // If no suggestions to filter, return empty array
  if (suggestions.length === 0) {
    return suggestions
  }

  // Call metadata-filter API to map suggestion values to canvas IDs
  try {
    if (!coreRestApiAxios?.value) {
      console.warn('Core REST API axios instance not available for system filter filtering')
      return suggestions
    }

    // Use all external filters to get accurate canvas ID mappings
    // We need the full filter context to correctly map suggestion values to canvas IDs
    const andConstraints = Object.entries(props.externalFilters || {})
      .filter(([, value]) => value !== null && value !== undefined && value !== '')
      .map(([key, value]) => ({
        key,
        value: Array.isArray(value) ? value.join(',') : String(value),
      }))

    const response = await coreRestApiAxios.value.post<unknown, Record<string, string[]>>('protected/metadata-filter', {
      key: props.filterKey,
      values: suggestions.map((s) => s.name),
      entity: props.entity || 'canvas',
      instance_id: pitcherInfo.value.instanceId,
      is_section: props.isSection,
      is_template: props.isTemplate,
      return_arrays: true,
      and: andConstraints.length > 0 ? andConstraints : undefined,
    })

    // Filter suggestions to only include those that have canvases in the system filter IDs
    const systemFilterIdSet = new Set(props.systemFilteredIds)
    const filteredSuggestions = suggestions.filter((suggestion) => {
      const canvasIds = response[suggestion.name]
      if (!canvasIds || !Array.isArray(canvasIds)) return false

      // Check if any of the canvases for this suggestion value are in the system filter IDs
      return canvasIds.some((canvasId) => systemFilterIdSet.has(canvasId))
    })

    return filteredSuggestions
  } catch (error) {
    console.error('Failed to filter suggestions by system filters:', error)
    // On error, return all suggestions rather than none
    return suggestions
  }
}

// Computed properties with clear naming
const filterName = computed(() => props.name || t('canvasUI.components.filters.searchLabel'))

// Convert distinct values to select options
const selectOptions = computed(() =>
  distinctValues.value.map((item) => ({
    label: item.usageCount ? `${item.displayName} (${item.usageCount})` : item.displayName,
    value: item.name,
  })),
)

// New suggestions function that shows distinct values
async function fetchDistinctValueSuggestions(searchTerm: string): Promise<StyledAutoCompleteOption[]> {
  // If we have all data (hasMoreData is false), filter locally
  if (!hasMoreData.value && distinctValues.value.length > 0) {
    // If no search term, show all distinct values
    if (!searchTerm) {
      return distinctValues.value.map((item) => ({
        label: item.usageCount ? `${item.displayName} (${item.usageCount})` : item.displayName,
        value: item.name,
      }))
    }

    // If there's a search term, filter the values
    const filtered = distinctValues.value
      .filter((item) => item.displayName.toLowerCase().includes(searchTerm.toLowerCase()))
      .map((item) => ({
        label: item.usageCount ? `${item.displayName} (${item.usageCount})` : item.displayName,
        value: item.name,
      }))

    return filtered
  }

  // If we don't have all data (hasMoreData is true), use the existing metadata search
  if (hasMoreData.value) {
    const searchResults = await handleFetch(searchTerm, props.entity, {
      includeDraftValues: props.includeDraftValues,
      isSection: props.isSection,
      isTemplate: props.isTemplate,
      key: props.filterKey,
      ownerId: props.ownerId,
      fetchAllUnique: true,
      tagsNotIn: props.tagsNotIn,
    })

    return searchResults
  }

  // If no search term and we have distinct values, show all distinct values (this case is handled above)
  // For API-based search, if no search term, return empty options since we should show suggestions only when typing
  return []
}

// Event handlers
async function onSearch(val: string) {
  emit('update:modelValue', val)
  isOpen.value = false

  // Re-fetch suggestions with updated filters
  if (isEnabled.value) {
    const result = await debouncedFetch(getConstraintFilters())
    if (result) {
      const filteredSuggestions = await filterSuggestionsBySystemFilters(result.suggestions)
      distinctValues.value = filteredSuggestions
      hasMoreData.value = result.hasMore || false
    }
  }
}

function onSelectUpdate(val: string) {
  emit('update:modelValue', val)
  isOpen.value = false
}

function onOutsideClick() {
  setTimeout(() => {
    isOpen.value = false
  }, 0)
}

async function onClearSearch() {
  searchOnClickRef.value?.clear()

  emit('update:modelValue', '')
  emit('clear')

  // Re-fetch all suggestions without constraints
  if (isEnabled.value) {
    const result = await debouncedFetch(getConstraintFilters())
    if (result) {
      const filteredSuggestions = await filterSuggestionsBySystemFilters(result.suggestions)
      distinctValues.value = filteredSuggestions
      hasMoreData.value = result.hasMore || false
    }
  }
}

// Watchers
watch(
  () => props.externalFilters,
  async (newFilters, oldFilters) => {
    if (!isEnabled.value) return

    // Always refresh for dependent fields when external filters change
    if (props.isDependentField) {
      const result = await debouncedFetch(getConstraintFilters())
      if (result) {
        const filteredSuggestions = await filterSuggestionsBySystemFilters(result.suggestions)
        distinctValues.value = filteredSuggestions
        hasMoreData.value = result.hasMore || false
      }
      return
    }

    // Also refresh for all components when filters are completely cleared
    // This ensures that when "Clear All Filters" is clicked, all components refresh with empty constraints
    const wasCleared =
      oldFilters && Object.keys(oldFilters).length > 0 && (!newFilters || Object.keys(newFilters).length === 0)

    if (wasCleared) {
      const result = await debouncedFetch(getConstraintFilters())
      if (result) {
        const filteredSuggestions = await filterSuggestionsBySystemFilters(result.suggestions)
        distinctValues.value = filteredSuggestions
        hasMoreData.value = result.hasMore || false
      }
    }
  },
  { deep: true },
)

// Lifecycle
onMounted(async () => {
  // Initial fetch of suggestions
  if (isEnabled.value) {
    const result = await debouncedFetch(getConstraintFilters())
    if (result) {
      // Update distinct values and hasMore flag
      const filteredSuggestions = await filterSuggestionsBySystemFilters(result.suggestions)
      distinctValues.value = filteredSuggestions
      hasMoreData.value = result.hasMore || false
    }
  }
})

// Watch for system filter changes to re-filter suggestions
watch(
  () => props.systemFilteredIds,
  async () => {
    if (!isEnabled.value) return

    const result = await debouncedFetch(getConstraintFilters())
    if (result) {
      const filteredSuggestions = await filterSuggestionsBySystemFilters(result.suggestions)
      distinctValues.value = filteredSuggestions
      hasMoreData.value = result.hasMore || false
    }
  },
)

onUnmounted(() => {
  cleanup()
})
</script>

<style scoped lang="scss">
.c-search-filter__popover {
  box-shadow: 0px 7px 24px 0px rgba(0, 0, 0, 0.1);
}
</style>
